import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tabs from '@/components/Tabs'
import { AuthType, type Item } from '@/components/Tabs/index.type'
import CustomTextInput from '@/components/CustomTextInput'
import OtherLoginItems from '@/components/OtherLoginItems' // 暂时未使用
import CustomButton from '@/components/CustomButton'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import { useTransactionRefresh } from '@/hooks'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'react-vant'
import { useSendCode } from '@/hooks/useSendCode'
import { APP_NAME } from '@/config/constants'
import SvgIcon from '@/components/SvgIcon'

export default React.memo(function Login() {

  const { refreshAfterTransaction } = useTransactionRefresh()
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<AuthType>()
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [countryCode, setCountryCode] = useState('+86')

  const handleCountryCodeChange = useCallback((code: string) => {
    setCountryCode(code)
    if (code !== '+86') {
      Toast.info(t('暂不支持该国家地区手机号注册'))
    }
  }, [t])
  const [password, setPassword] = useState('')
  const [isAgreed, setIsAgreed] = useState(false)
  const [errors, setErrors] = useState<{ email?: string, phone?: string, password?: string }>({})
  const [loading, setLoading] = useState(false)
  const { sendCode, sendCodeLoading } = useSendCode()

  // 登录类型选项
  const loginTypeItems = useMemo<Item<AuthType>[]>(() => [
    { type: AuthType.Email, text: t('邮箱') },
    { type: AuthType.Phone, text: t('手机号') }
  ], [t])

  // 邮箱校验函数 - 参考注册页面
  const validateEmail = useCallback((emailValue: string) => {
    if (!emailValue) {
      return t('请输入邮箱地址')
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      return t('请输入有效的邮箱地址')
    }
    return ''
  }, [t])

  // 密码校验函数 - 参考设置密码页面
  const validatePassword = useCallback((pwd: string) => {
    if (!pwd) {
      return t('请输入密码')
    }
    if (pwd.length < 8) {
      return t('密码长度不能少于8个字符')
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])/.test(pwd)) {
      return t('密码必须包含大小写字母')
    }
    return ''
  }, [t])

  // 手机号验证函数
  const validatePhone = useCallback((phoneValue: string) => {
    if (!phoneValue) {
      return t('请输入手机号')
    }
    // 验证纯数字手机号：6-15位数字
    const phoneRegex = /^\d{6,15}$/
    if (!phoneRegex.test(phoneValue)) {
      return t('请输入有效的手机号6至15位数字')
    }
    return ''
  }, [t])

  // 邮箱输入处理
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setErrors(prev => ({ ...prev, email: error }))
  }, [validateEmail])

  // 密码输入处理
  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value)
    const error = validatePassword(value)
    setErrors(prev => ({ ...prev, password: error }))
  }, [validatePassword])

  // 手机号输入处理
  const handlePhoneChange = useCallback((value: string) => {
    // 只允许输入数字
    const numericValue = value.replace(/[^\d]/g, '')
    setPhone(numericValue)
    const error = validatePhone(numericValue)
    setErrors(prev => ({ ...prev, phone: error }))
  }, [validatePhone])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    if (selectedType === AuthType.Email) {
      return email && password && !validateEmail(email) && !validatePassword(password)
    }
    if (selectedType === AuthType.Phone) {
      return phone && !validatePhone(phone)
    }
    return false
  }, [selectedType, email, password, phone, validateEmail, validatePassword, validatePhone])

  // 邮箱登录
  const handleEmailLogin = useCallback(async () => {
    if (!isFormValid || !isAgreed) return

    setLoading(true)
    setErrors({ email: '', password: '' })

    try {
      const data = await APIWeb.login({
        account: email,
        password,
      })
      localStorage.setItem('token', data.token || '')
      await refreshAfterTransaction()

      Toast.success({
        message: t('登录成功'),
        forbidClick: true,
        onClose: () => navigate('/home')
      })
    } catch (error) {
      setPassword('')
      console.error('登录失败', error)
    } finally {
      setLoading(false)
    }
  }, [email, password, isFormValid, isAgreed, navigate])

  // 手机号登录 - 发送验证码
  const handlePhoneLogin = useCallback(async () => {
    if (!isFormValid || !isAgreed) return

    const phoneError = validatePhone(phone)
    if (phoneError) {
      setErrors(prev => ({ ...prev, phone: phoneError }))
      return
    }

    // 发送验证码成功后跳转到验证码页面
    const data = await sendCode({
      account: phone,
      scene: 'register', // 使用 register scene
    })

    if (data) {
      navigate('/login/verify-code', {
        state: {
          account: phone,
          type: 'phone',
          countryCode
        }
      })
    }
  }, [phone, countryCode, isFormValid, isAgreed, sendCode, navigate, validatePhone])

  // 统一登录处理
  const handleLogin = useCallback(async () => {
    if (selectedType === AuthType.Email) {
      await handleEmailLogin()
    } else if (selectedType === AuthType.Phone) {
      await handlePhoneLogin()
    }
  }, [selectedType, handleEmailLogin, handlePhoneLogin])

  const onChange = useCallback((type: AuthType) => {
    setSelectedType(type)
    // 切换时清空错误信息
    setErrors({})
  }, [])

  const EmailLoginView = useMemo(
    () => (
      <div className="space-y-5">
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">{t('您的电子邮箱')}</div>
          <CustomTextInput
            value={email}
            onChangeText={handleEmailChange}
            placeholder={t('请输入您的电子邮箱')}
            isErrored={Boolean(errors.email)}
            inputMode='email'
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">{t('密码')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.password)}
            value={password}
            onChangeText={handlePasswordChange}
            placeholder={t('请输入您的密码')}
            secureTextEntry={true}
            isShowSmallEyes={true}
          />
          {errors.password && (
            <p className="mt-1 text-xs text-red-600">{errors.password}</p>
          )}
        </div>
      </div>
    ),
    [email, password, handleEmailChange, handlePasswordChange, errors.email, errors.password, t]
  )

  const PhoneLoginView = useMemo(
    () => (
      <div className="space-y-5">
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">{t('您的手机号')}</div>
          <div className={`flex items-center h-12 rounded-2.5 border border-solid bg-white ${errors.phone ? 'border-red-500' : 'border-[#EAECF0]'}`}>
            <div className="h-full flex items-center pl-4 pr-2 [&>div]:border-none [&>div]:h-auto [&>div]:p-0 [&>div]:rounded-none cursor-pointer">
              <CountryCodeSelector
                value={countryCode}
                onChange={handleCountryCodeChange}
              />
            </div>
            <input
              type="tel"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder={t('请输入您的手机号')}
              inputMode="numeric"
              className="flex-1 h-full px-3 outline-none text-[#1D2129] placeholder-[#C9CDD4] bg-transparent"
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
          )}
        </div>
      </div>
    ),
    [phone, countryCode, handleCountryCodeChange, handlePhoneChange, errors.phone, t]
  )

  const LoginViewRender = useMemo(
    () => (
      selectedType === AuthType.Email ? EmailLoginView : PhoneLoginView
    ),
    [selectedType, EmailLoginView, PhoneLoginView]
  )

  const handleAgreementToggle = useCallback(() => {
    setIsAgreed(prev => !prev)
  }, [])

  // Google 登录处理
  const handleGoogleLogin = useCallback(async (idToken: string) => {
    const toastInstance = Toast.loading({
      message: t('正在登录中...'),
      forbidClick: true,
      duration: 0,
    })

    try {
      const data = await APIWeb.googleLogin({ id_token: idToken })

      if (data?.token) {
        localStorage.setItem('token', data.token)
        await refreshAfterTransaction()
        toastInstance.clear()

        Toast.success({
          message: t('登录成功'),
          forbidClick: true,
          onClose: () => navigate('/home', { replace: true })
        })
      } else {
        toastInstance.clear()
        Toast.fail(t('登录失败，请重试'))
      }
    } catch (err: any) {
      console.error('Google登录失败:', err)
      toastInstance.clear()
      Toast.fail(err?.message || t('登录失败，请重试'))
    }
  }, [navigate, refreshAfterTransaction, t])

  return (
    <div className="bg-white h-full">
      <div className="overflow-y-auto">
        <div>
          {/* 多语言切换按钮 */}
          <div className="w-full flex justify-end px-5 pt-5"
            onClick={() => navigate('/set-lang')}>
            <SvgIcon
              className="cursor-pointer"
              src="/icon/lang-blue.svg"
              color="var(--primary-color)"
              size={20}
            />
            <span className="text-sm color-[var(--primary-color)] ml-2">{t('语言')}</span>
          </div>

          {/* Logo */}
          <div className="w-full pt-14 pb-8 flex items-center justify-center">
            <img className="w-34" src="/icon/logo.svg" alt="Logo" />
          </div>

          <div className="px-6">
            <Tabs<AuthType> items={loginTypeItems} defaultSelected={AuthType.Email} onChange={onChange} />
          </div>

          <div className="px-6 mt-5">
            {LoginViewRender}
          </div>

          <div className="px-6 mt-5 flex items-center justify-between">
            <div className="flex items-center">
              <div className='flex-shrink-0 mr-2'>
                <Checkbox
                  shape='square'
                  checkedColor={"var(--primary-color)"}
                  checked={isAgreed}
                  onChange={handleAgreementToggle}
                />
              </div>
              <span className="text-sm text-gray-600">{t('我同意')}</span>
              <Link to="/privacy-policy" className="text-sm text-[var(--primary-color)]">
                {t('隐私政策')}
              </Link>
            </div>
            <Link to="/unable-login" className="text-sm text-[var(--primary-color)] text-right">
              {t('无法登录问号')}
            </Link>
          </div>

          <div className="px-6 mt-5">
            <CustomButton
              onClick={handleLogin}
              loading={loading || sendCodeLoading}
              disabled={!isAgreed || !isFormValid || loading || sendCodeLoading}
            >
              {t('开始使用')}
            </CustomButton>
          </div>

          <div className="px-6 mt-6 flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">{t('{{name}}新用户', { name: APP_NAME })}</span>
            <Link to="/register" className="text-sm text-[var(--primary-color)]">
              {t('注册')}
            </Link>
          </div>
        </div>

        {/* Other login options */}
        <div className="px-6 mt-9 pb-8">
          <OtherLoginItems onGoogleLogin={handleGoogleLogin} />
        </div>
      </div>

    </div>
  )
})